import { isEmpty, identity } from 'lodash'

import {
  ADD_CARD_FROM_IDEA_BOARD,
  SET_IDEAS,
  SET_THREADS,
  DISPLAY_POCKET_IDEA,
  HIDE_IDEA_BOARD,
  ADD_AUDIO_CLIP_FROM_IDEA_BOARD,
} from '../constants/ActionTypes'
import { jumpFromChipToItem, openTempCardModal } from './ui'
import { addImage } from './images'
import selectors from '../selectors/index'
import { isStoragePath } from '../helpers/file'
import { _addNoteFromIdeaBoard, _addPlaceFromIdeaBoard, _addCharacterFromIdeaBoard } from './ui'
import { toUniversalIdeaId } from '../types/plottr-in-your-pocket'

const {
  imagesSelector,
  allPlacesSelector,
  allNotesSelector,
  allCharactersSelector,
  allAudioClipsSelector,
  allCardsSelector,
} = selectors(identity)

/**
 * @typedef {ReturnType<typeof setThreads> | ReturnType<typeof setIdeas> | ReturnType<typeof displayPocketIdea> | ReturnType<typeof _hideIdeaBoard> | ReturnType<typeof _displayPocketIdea> | ReturnType<typeof _addCardFromIdeaBoard> | ReturnType<typeof addAudioClipFromIdeaBoard>} IdeasActions
 */

/**
 * @typedef CustomAttribute
 * @property {String} name
 * @property {String | Slate | number} value
 * @property {'text' | 'paragraph' | 'audio'} type
 */

/**
 * @typedef AttributeValue
 * @property {number} id
 * @property {String | Slate | number} value
 * @property {number | String} [bookId]
 */

/**
 * @typedef AttributeMetadata
 * @property {number} id
 * @property {String} name
 * @property {'audio' | 'text' | 'paragraph'} type
 */

/**
 * @typedef {import('../types/system').Ideas} Ideas
 * @typedef {import('../types/system').Threads} Threads
 * @typedef {import('../types/system').Idea} Idea
 * @typedef {import('../types/plottr-in-your-pocket').UniversalIdeaId} UniversalIdeaId
 * @typedef {import('../types/plottr').IdeaBoardEntityKind} IdeaBoardEntityKind
 * @typedef {import('../types/plottr').AudioClipRecord} AudioClipRecord
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').Dispatch} Dispatch
 * @typedef {import('../index').GetState} GetState
 * @typedef {import('../index').Slate} Slate
 * @typedef {import('../index').Card} Card
 */

/**
 * Overwrite the current value of the ideas in the system store.
 *
 * @param {Ideas} ideas
 * @return {{ type: 'SET_IDEAS', ideas: Ideas }}
 */
export const setIdeas = (ideas) => {
  return {
    type: SET_IDEAS,
    ideas,
  }
}

/**
 * Overwrite the current value of the threads in the system store.
 *
 * @param {Threads} threads
 * @return {{ type: 'SET_THREADS', threads: Threads }}
 */
export const setThreads = (threads) => {
  return {
    type: SET_THREADS,
    threads,
  }
}

/**
 * Signal that a pocket idea should be displayed.  This action isn't
 * part of UI state because there isn't an expectation that each
 * client have the same pocket ideas available nor open.
 *
 * @param {UniversalIdeaId | null} pocketIdeaId
 * @return {{ type: 'DISPLAY_POCKET_IDEA', pocketIdeaId: UniversalIdeaId | null }}
 */
export const displayPocketIdea = (pocketIdeaId) => {
  return {
    type: DISPLAY_POCKET_IDEA,
    pocketIdeaId,
  }
}

/**
 * @typedef {any & { id: number }} HasId
 */

/**
 * @param {Array<HasId>} index
 * @return {number}
 */
const idOfLatestEntityAdded = (index) => {
  return index.reduce((maxId, next) => {
    return Math.max(maxId, next.id)
  }, 0)
}

/**
 * @param {Record<String, HasId>} index
 * @return {number}
 */
const nextId = (index) => {
  return idOfLatestEntityAdded(Object.values(index)) + 1
}

/**
 * @return {{ type: 'HIDE_IDEA_BOARD' }}
 */
export const _hideIdeaBoard = () => {
  return { type: HIDE_IDEA_BOARD }
}

/**
 * @param {UniversalIdeaId | Null} pocketIdeaId
 * @return {{ type: 'DISPLAY_POCKET_IDEA', pocketIdeaId: UniversalIdeaId | Null }}
 */
export const _displayPocketIdea = (pocketIdeaId) => {
  return {
    type: DISPLAY_POCKET_IDEA,
    pocketIdeaId,
  }
}

/**
 * Create a new Plottr entity based on the supplied pocket idea.
 * Specify the type of entity as the first argument.
 *
 * @param {IdeaBoardEntityKind} kind
 * @param {Idea} idea
 * @return {function(Dispatch, GetState): Void}
 */
export const savePocketIdeaAs = (kind, idea) => (dispatch, getState) => {
  const description = idea.kind === 'text' ? idea.data : idea.description
  const audioClips = allAudioClipsSelector(getState())
  const existingAudioClip =
    idea.kind === 'audio'
      ? Object.values(audioClips).find((audioClip) => {
          return audioClip.path === idea.data.uri || audioClip.data === idea.data.uri
        })
      : null
  const nextAudioClipId = nextId(audioClips)
  if (idea.kind === 'audio' && !existingAudioClip) {
    const isOnStorage = isStoragePath(idea.data.uri)
    const data = isOnStorage ? '' : idea.data.uri
    const path = isOnStorage ? idea.data.uri : ''
    dispatch(
      addAudioClipFromIdeaBoard({
        id: nextAudioClipId,
        name: idea.name,
        data,
        path,
      })
    )
  }
  const images = imagesSelector(getState())
  const existingPicture =
    idea.kind === 'photo'
      ? Object.values(images).find((image) => {
          return image.path === idea.data.uri || image.data === idea.data.uri
        })
      : null
  const nextImageId = nextId(images)
  if (idea.kind === 'photo' && !existingPicture) {
    const isOnStorage = isStoragePath(idea.data.uri)
    const data = isOnStorage ? '' : idea.data.uri
    const path = isOnStorage ? idea.data.uri : ''
    dispatch(
      addImage({
        id: nextImageId,
        name: idea.name,
        data,
        path,
      })
    )
  }
  const picture =
    idea.kind === 'photo'
      ? !existingPicture
        ? nextImageId.toString()
        : existingPicture.id.toString()
      : null
  const audioClip =
    idea.kind === 'audio' ? (!existingAudioClip ? nextAudioClipId : existingAudioClip.id) : null
  const universalIdeaId = toUniversalIdeaId(idea.deviceId, idea.id)
  if (kind === 'places') {
    // We only support text and audio custom attributes for now
    addPlaceFromIdeaBoard(
      idea.name || '',
      description,
      picture,
      audioClip,
      universalIdeaId
    )(dispatch, getState)
    const allPlaces = allPlacesSelector(getState())
    const id = idOfLatestEntityAdded(allPlaces)
    dispatch(_hideIdeaBoard())
    dispatch(_displayPocketIdea(null))
    jumpFromChipToItem('places', id)(dispatch, getState)
  } else if (kind === 'notes') {
    // We only support text and audio custom attributes for now
    addNoteFromIdeaBoard(
      idea.name || '',
      description,
      picture,
      audioClip,
      universalIdeaId
    )(dispatch, getState)
    const allNotes = allNotesSelector(getState())
    const id = idOfLatestEntityAdded(allNotes)
    dispatch(_hideIdeaBoard())
    dispatch(_displayPocketIdea(null))
    jumpFromChipToItem('notes', id)(dispatch, getState)
  } else if (kind === 'cards') {
    // NOTE: We only support text and audio custom attributes for now
    openTempCardModal({
      title: idea.name || '',
      description,
      audioClipId: audioClip,
      pocketIdeaId: universalIdeaId,
    })(dispatch, getState)
    dispatch(_hideIdeaBoard())
    dispatch(_displayPocketIdea(null))
  } else if (kind === 'characters') {
    addCharacterFromIdeaboard(
      idea.name || '',
      description,
      picture,
      audioClip,
      universalIdeaId
    )(dispatch)
    const allCharacters = allCharactersSelector(getState())
    const id = allCharacters.reduce((maxId, next) => {
      return Math.max(maxId, next.id)
    }, 0)
    dispatch(_hideIdeaBoard())
    dispatch(_displayPocketIdea(null))
    jumpFromChipToItem('characters', id)(dispatch, getState)
  }
}

/**
 * Create a place from an idea.  If the idea comes from the mobile
 * app, it might also have a picture and some custom attributes
 * associated with it.
 *
 * @param {String} name
 * @param {String | Slate} description
 * @param {String | Null} picture
 * @param {number | Null} audioClip
 * @param {UniversalIdeaId} [pocketIdeaId]
 * @return {function(Dispatch, GetState): Void}
 */
export const addPlaceFromIdeaBoard =
  (name, description, picture, audioClip, pocketIdeaId) => (dispatch, getState) => {
    const allPlaces = allPlacesSelector(getState())
    const newId = nextId(allPlaces)

    dispatch(_addPlaceFromIdeaBoard(name, description, newId, picture, audioClip, [], pocketIdeaId))
  }

/**
 * Create a note from an idea.  If the idea comes from the mobile
 * app, it might also have a picture and some custom attributes
 * associated with it.
 *
 * @param {String} title
 * @param {String | Slate} content
 * @param {String | Null} picture
 * @param {number | Null} audioClip
 * @param {UniversalIdeaId} [pocketIdeaId]
 * @return {function(Dispatch, GetState): Void}
 */
export const addNoteFromIdeaBoard =
  (title, content, picture, audioClip, pocketIdeaId) => (dispatch, getState) => {
    const allNotes = allNotesSelector(getState())
    const newId = nextId(allNotes)

    dispatch(
      _addNoteFromIdeaBoard(
        title,
        content,
        newId,
        picture,
        [],
        audioClip,
        new Date().getTime(),
        pocketIdeaId
      )
    )
  }

/**
 * @param {Partial<Card>} card
 * @param {number} id
 * @param {UniversalIdeaId} [pocketIdeaId]
 * @return {{ type: 'ADD_CARD_FROM_IDEA_BOARD', card: Partial<Card>, id: number, pocketIdeaId: UniversalIdeaId | undefined }}
 */
export const _addCardFromIdeaBoard = (card, id, pocketIdeaId) => {
  return {
    type: ADD_CARD_FROM_IDEA_BOARD,
    card,
    id,
    pocketIdeaId,
  }
}

/**
 * @param {Partial<Card>} attributes
 * @param {UniversalIdeaId} [pocketIdeaId]
 * @return {function(Dispatch, GetState): Void}
 */
export const addCardFromIdeaBoard = (attributes, pocketIdeaId) => (dispatch, getState) => {
  if (isEmpty(attributes)) {
    return
  } else {
    const allCards = allCardsSelector(getState())
    const newId = nextId(allCards)
    dispatch(_addCardFromIdeaBoard(attributes, newId, pocketIdeaId))
  }
}

/**
 * Create a character from an idea.  If the idea comes from the mobile
 * app it might also have a picture and some custom attributes
 * associated with it.
 *
 * @param {String} name
 * @param {String | Slate} description
 * @param {String | Null} picture
 * @param {number | Null} audioClip
 * @param {UniversalIdeaId} [pocketIdeaId]
 * @return {function(Dispatch): Void}
 */
export const addCharacterFromIdeaboard =
  (name, description, picture, audioClip, pocketIdeaId) => (dispatch) => {
    dispatch(
      // NOTE(ed): We add extra values to the action in the root
      // reducer.
      //
      // @ts-ignore
      _addCharacterFromIdeaBoard(name, description, picture, audioClip, [], pocketIdeaId)
    )
  }

/**
 * @param {AudioClipRecord} audioClip
 * @return {{ type: 'ADD_AUDIO_CLIP_FROM_IDEA_BOARD', audioClip: AudioClipRecord }}
 */
export function addAudioClipFromIdeaBoard(audioClip) {
  return {
    type: ADD_AUDIO_CLIP_FROM_IDEA_BOARD,
    audioClip,
  }
}
